import React, { FC, useEffect, useRef, useState } from 'react';
import { useQueryParams } from '../../@shared/hooks/useQueryParams';
import { useOrders } from '../../hooks/collections/useOrdersV2';
import { firebase } from '../../firebase/config';
import { useLoader } from '../../@shared/hooks/useLoader';
import moment from 'moment';
import { useAlert } from '../../@shared/hooks/useAlert';
import { useNavigate } from 'react-router-dom';
import { OrderStatusEnum } from '../../enums/OrderStatus.enum';
import schoonLogo from 'images/schoon_transparent.svg';
import { useReservations } from '../../hooks/collections/useReservations';
import { useFirebaseData } from '../../@shared/hooks/useFirebaseData';
import { Collections } from '../../@core/constants/FirebasePaths';

export interface CheckOutPagePageMainProps {
  className?: string;
}

const cardsIcons = [
  {
    id: 1,
    name: 'Visa',
    icon: 'https://firebasestorage.googleapis.com/v0/b/certificame-dev.firebasestorage.app/o/vs.png?alt=media&token=34b707ae-06ae-45ce-8aed-e7e92d2715b9'
  },
  {
    id: 2,
    name: 'Mastercard',
    icon: 'https://firebasestorage.googleapis.com/v0/b/certificame-dev.firebasestorage.app/o/mc.png?alt=media&token=22747b66-52a1-4a9c-80b5-47e1a8c3478f'
  },
  {
    id: 4,
    name: 'Discover',
    icon: 'https://firebasestorage.googleapis.com/v0/b/certificame-dev.firebasestorage.app/o/dis.png?alt=media&token=3602d84f-2203-46cf-a231-bc2878e6bc57'
  },
  {
    id: 5,
    name: 'Diners Club',
    icon: 'https://firebasestorage.googleapis.com/v0/b/certificame-dev.firebasestorage.app/o/din.png?alt=media&token=f2a58538-0f4d-43ef-a8c8-18901a43b9f7'
  },
  {
    id: 6,
    name: 'JCB',
    icon: 'https://firebasestorage.googleapis.com/v0/b/certificame-dev.firebasestorage.app/o/jcb.png?alt=media&token=171e5778-0e09-46e4-b415-9a75541fe615'
  }
];

const CheckOutPagePageMain: FC<CheckOutPagePageMainProps> = ({
  className = ''
}) => {
  const orderId = useQueryParams('orderId') as string;
  const db = useFirebaseData({ collectionPath: null, loadData: false });
  const { reservations } = useReservations({
    loadData: true, queries: [
      {
        type: 'where',
        field: 'order',
        operator: '==',
        value: db.getReference(`${Collections.orders._path}/${orderId}`)
      }
    ]
  })

  const { getOrder, updateOrder } = useOrders({ loadData: true });
  const [order, setOrder] = useState<any>(null);
  const loader = useLoader();
  const [remainingTime, setRemainingTime] = useState<number>(0);
  const intervalRef = useRef(null);
  const alert = useAlert();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  const handleSubmitPayment = async () => {
    loader.start();
    try {
      const checkoutFunction = firebase.functions().httpsCallable('api/user/checkout');
      const response = await checkoutFunction({
        userKey: order.createdBy.id,
        orderId
      })
      await updateOrder(order.id, { stripeSessionId: response.data.id });
      window.open(response.data.url, '_blank');
    } catch (e) {
      console.log(e);
    } finally {
      loader.stop();
    }
  };

  useEffect(() => {
    if (orderId) getOrder(orderId).then((res) => {
      if (res?.status !== OrderStatusEnum.GENERATED) navigate(-1);
      setOrder(res);
      intervalRef.current = setInterval(() => {
        const now = Date.now();
        const secondsIn15Min = 900;
        const diffInSeconds = Math.max(secondsIn15Min - Math.floor((now - res.createdAt) / 1000), 0);

        if (diffInSeconds <= 0) {
          clearInterval(intervalRef.current);
          navigate(`/service-detail/${res.service.id}`);
          alert.error('Time Expired', 'Your payment time has expired. The reservation for the day and hour has been reopened.');
        }
        setRemainingTime(diffInSeconds);
      }, 10);
    }).finally(() => setIsLoading(false));

    return () => clearInterval(intervalRef.current);
  }, [orderId]);

  if (!order) return <div className={'min-h-screen'}></div>;

  const renderSidebar = () => {
    return (
      <div className="w-full flex flex-col sm:rounded-2xl lg:border border-neutral-200 dark:border-neutral-700 space-y-6 sm:space-y-8 px-0 sm:p-6 xl:p-8">

        <div className="flex flex-col space-y-4">
          <h3 className="text-2xl font-semibold">Price detail</h3>
          <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
            <span>Service(s)</span>
            <span>${order.total}</span>
          </div>
          <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
            <span>Tax</span>
            <span>$0</span>
          </div>

          <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
          <div className="flex justify-between font-semibold">
            <span>Total</span>
            <span>${order.total}</span>
          </div>

          <div className="mt-3">
            <div className="pt-2">
              {stripeButton()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const stripeButton = () => {
    return (
      <>
        <button
          onClick={handleSubmitPayment}
          className={'w-full text-white flex items-center justify-center gap-1 mt-5 bg-amber-500 hover:shadow-sm transition duration-300 rounded-full p-3 font-semibold'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-4">
            <path d="M4.5 3.75a3 3 0 0 0-3 3v.75h21v-.75a3 3 0 0 0-3-3h-15Z"/>
            <path
              fill-rule="evenodd"
              d="M22.5 9.75h-21v7.5a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3v-7.5Zm-18 3.75a.75.75 0 0 1 .75-.75h6a.75.75 0 0 1 0 1.5h-6a.75.75 0 0 1-.75-.75Zm.75 2.25a.75.75 0 0 0 0 1.5h3a.75.75 0 0 0 0-1.5h-3Z"
              clip-rule="evenodd"
            />
          </svg>
          Pay with<span className={'text-violet-600 font-black ms-1'}> Stripe</span>
        </button>
        <div className={'flex flex-wrap items-center justify-center mt-5 gap-2 w-full'}>
          {cardsIcons.map((logo) => (
            <img
              key={logo.id}
              src={logo.icon}
              alt={logo.name}
              className={'lg:max-w-14 max-w-16 rounded-xl object-cover'}
            />
          ))}
        </div>
      </>
    );
  };

  const renderMain = () => {
    return (
      <div className="w-full flex flex-col sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-8 px-0 sm:p-6 xl:p-8">
        <h2 className="text-3xl lg:text-4xl font-semibold">
          Confirm and payment
        </h2>
        <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
        <div>
          <div>
            <h3 className="text-2xl font-light">Your reservation(s):</h3>
            <div className="flex flex-col sm:flex-row sm:items-center mt-5">
              <div className="flex-shrink-0 w-full sm:w-20">
                <div className=" aspect-w-4 aspect-h-3 sm:aspect-h-4 rounded-2xl overflow-hidden">
                  <img
                    alt=""
                    className="absolute inset-0 object-cover"
                    sizes="200px"
                    src={order?.service?.images?.length ? order.service.images[0] : schoonLogo}
                  />
                </div>
              </div>
              <div className="py-5 sm:px-5 space-y-3">
                <div>
              <span className="text-sm text-neutral-500 dark:text-neutral-400 line-clamp-1">
                {order.service.title}
              </span>
                  <span className="text-base font-medium mt-1 block">
                {order.service.details.location.address}
              </span>
                </div>
              </div>
            </div>

            <div className="mt-5 space-y-1">
              {reservations?.length ? (
                reservations.map((r: any) => (
                  <div key={r.id} className="text-base">
                    <span className="font-semibold">{r.day} - {r.hour} hrs.</span>
                  </div>
                ))
              ) : (
                <div className="text-sm opacity-70">No reservations found.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`nc-CheckOutPagePageMain ${className}`}>
      {!isLoading ? (
        <>
          <div className={'text-center my-5'}>Remaining
            time: <span className={'text-lg font-black'}>{moment.utc(remainingTime * 1000).format('mm:ss')}</span></div>
          <div className={'text-center text-sm opacity-80 font-light'}>Day and hour will reopen after the time remaining
            expires.
          </div>
          <main className="container mt-11 mb-24 lg:mb-32 flex flex-col lg:flex-row">
            <div className="w-full lg:w-3/5 xl:w-2/3 lg:pr-10">{renderMain()}</div>
            <div className="block flex-grow">{renderSidebar()}</div>
          </main>
        </>
      ) : null}
    </div>
  );
};

export default CheckOutPagePageMain;
